import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';

export default function SalesRevenue() {
  const { user } = useAuth();
  const [data, setData] = useState({ orders: [], totalRevenue: 0, summary: { successfulCount: 0, failedCount: 0, cancelledCount: 0 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rangeStart, setRangeStart] = useState(''); // YYYY-MM-DD
  const [rangeEnd, setRangeEnd] = useState(''); // YYYY-MM-DD

  const [savedFilters, setSavedFilters] = useState([]); // {id,name,startDate,endDate,createdAt}
  const [selectedSavedId, setSelectedSavedId] = useState('');
  const [newFilterName, setNewFilterName] = useState('');
  const role = user && String(user.role).toLowerCase();
  const isSalesUser = role === 'custom_role_sales';
  const isAdmin = role === 'admin' || role === 'master_admin';

  const storageKey = useMemo(() => {
    const who = user?._id || user?.id || user?.email || 'default';
    return `salesRevenueSavedRanges:${who}`;
  }, [user]);

  useEffect(() => {
    if (!isAdmin) return;
    try {
      const raw = localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      setSavedFilters(Array.isArray(parsed) ? parsed : []);
    } catch {
      setSavedFilters([]);
    }
  }, [isAdmin, storageKey]);

  const persistSavedFilters = useCallback((next) => {
    setSavedFilters(next);
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // ignore storage errors
    }
  }, [storageKey]);

  const computedParams = useMemo(() => {
    if (!isAdmin) return null;
    if (!rangeStart || !rangeEnd) return null;
    return { startDate: rangeStart, endDate: rangeEnd };
  }, [isAdmin, rangeStart, rangeEnd]);

  const hasActiveRange = isAdmin && rangeStart && rangeEnd;

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const params = isAdmin ? (computedParams || undefined) : undefined;
      const res = await apiService.orders.getSalesOrdersRevenue(params);
      setData({
        orders: res.data?.orders || [],
        totalRevenue: res.data?.totalRevenue ?? 0,
        summary: res.data?.summary || { successfulCount: 0, failedCount: 0, cancelledCount: 0 },
        compare: undefined,
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load revenue');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, computedParams]);

  useEffect(() => {
    load();
  }, [load]);

  const applyRange = useCallback(() => {
    if (!rangeStart || !rangeEnd) return;
    if (rangeStart > rangeEnd) {
      setError('Start date must be before end date.');
      return;
    }
    setSelectedSavedId('');
    load();
  }, [rangeStart, rangeEnd, load]);

  const clearRange = useCallback(() => {
    setRangeStart('');
    setRangeEnd('');
    setSelectedSavedId('');
  }, []);

  const handleSelectSaved = useCallback((id) => {
    setSelectedSavedId(id);
    const found = savedFilters.find((f) => f.id === id);
    if (!found) return;
    setRangeStart(found.startDate);
    setRangeEnd(found.endDate);
  }, [savedFilters]);

  const handleSaveCurrent = useCallback(() => {
    if (!rangeStart || !rangeEnd) {
      setError('Pick start and end dates first.');
      return;
    }
    if (!newFilterName.trim()) {
      setError('Please enter a name for this filter.');
      return;
    }
    if (rangeStart > rangeEnd) {
      setError('Start date must be before end date.');
      return;
    }
    const id = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const next = [
      { id, name: newFilterName.trim(), startDate: rangeStart, endDate: rangeEnd, createdAt: new Date().toISOString() },
      ...savedFilters,
    ];
    persistSavedFilters(next);
    setNewFilterName('');
    setSelectedSavedId(id);
  }, [rangeStart, rangeEnd, newFilterName, savedFilters, persistSavedFilters]);

  const handleDeleteSaved = useCallback(() => {
    if (!selectedSavedId) return;
    const found = savedFilters.find((f) => f.id === selectedSavedId);
    if (!found) return;
    const ok = window.confirm(`Delete saved filter "${found.name}"?`);
    if (!ok) return;
    const next = savedFilters.filter((f) => f.id !== selectedSavedId);
    persistSavedFilters(next);
    setSelectedSavedId('');
  }, [selectedSavedId, savedFilters, persistSavedFilters]);

  const formatDateTime = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const hoursStr = String(hours).padStart(2, '0');
    // For sales users, show only time (today only). For admins, show date + time.
    if (isSalesUser) {
      return `${hoursStr}:${minutes} ${ampm}`;
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day} / ${month} / ${year} ${hoursStr}:${minutes} ${ampm}`;
  };

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="bg-white rounded-lg shadow-md border-l-4 border-green-500 p-3 sm:p-4 md:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-700 mb-2 sm:mb-4">Revenue</h1>
        <p className="text-gray-600 text-sm mb-2">
          {isSalesUser
            ? 'Revenue from orders where payment has been received — today only.'
            : isAdmin
            ? 'Revenue from sales orders where payment has been received. View all or filter by date.'
            : 'Revenue from sales orders where payment has been received.'}
        </p>
        {isAdmin && (
          <div className="mb-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="text-sm font-medium text-gray-700 mb-2">Date range</div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="date"
                  value={rangeStart}
                  onChange={(e) => setRangeStart(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-gray-700"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-gray-700"
                />
              </div>
              <div className="mt-2 flex items-center gap-3">
                <button
                  type="button"
                  onClick={applyRange}
                  disabled={!rangeStart || !rangeEnd || loading}
                  className="px-3 py-2 rounded-lg bg-[#735dff] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply
                </button>
                <button
                  type="button"
                  onClick={clearRange}
                  disabled={loading}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear (All time)
                </button>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="text-sm font-medium text-gray-700 mb-2">Save current range</div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  value={newFilterName}
                  onChange={(e) => setNewFilterName(e.target.value)}
                  placeholder="e.g. April Week 2"
                  className="border border-gray-300 rounded-lg px-3 py-2 text-gray-700 min-w-[220px]"
                />
                <button
                  type="button"
                  onClick={handleSaveCurrent}
                  disabled={!rangeStart || !rangeEnd || loading}
                  className="px-3 py-2 rounded-lg bg-green-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save
                </button>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {hasActiveRange ? `Current: ${rangeStart} → ${rangeEnd}` : 'Pick a range to save it.'}
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 lg:col-span-2">
              <div className="text-sm font-medium text-gray-700 mb-2">Saved filters</div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={selectedSavedId}
                  onChange={(e) => handleSelectSaved(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-gray-700 min-w-[260px]"
                >
                  <option value="">Select saved filter…</option>
                  {savedFilters.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name} ({f.startDate} → {f.endDate})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={applyRange}
                  disabled={!rangeStart || !rangeEnd || loading}
                  className="px-3 py-2 rounded-lg bg-[#735dff] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply
                </button>
                <button
                  type="button"
                  onClick={handleDeleteSaved}
                  disabled={!selectedSavedId || loading}
                  className="px-3 py-2 rounded-lg bg-red-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Delete
                </button>
              </div>
              <div className="mt-2 text-xs text-gray-500">Saved filters are stored in this browser.</div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        {loading && <div className="py-8 text-center text-gray-500">Loading…</div>}
        {!loading && (
          <>
            <div className="mb-4 grid grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="bg-white border rounded-lg p-3">
                <div className="text-xs text-gray-500">Successful orders (Paid)</div>
                <div className="text-lg sm:text-xl font-bold text-gray-800 tabular-nums">
                  {Number(data.summary?.successfulCount || 0)}
                </div>
              </div>
              <div className="bg-white border rounded-lg p-3">
                <div className="text-xs text-gray-500">Failed orders</div>
                <div className="text-lg sm:text-xl font-bold text-gray-800 tabular-nums">
                  {Number(data.summary?.failedCount || 0)}
                </div>
              </div>
              <div className="bg-white border rounded-lg p-3">
                <div className="text-xs text-gray-500">Cancelled orders</div>
                <div className="text-lg sm:text-xl font-bold text-gray-800 tabular-nums">
                  {Number(data.summary?.cancelledCount || 0)}
                </div>
              </div>
            </div>
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="text-sm text-gray-600">
                Total revenue
              </div>
              <div className="text-xl sm:text-2xl font-bold text-green-800 tabular-nums">SAR {Number(data.totalRevenue).toFixed(2)}</div>
            </div>
            {data.orders.length === 0 ? (
              <p className="text-gray-500 py-4">No orders with payment received yet.</p>
            ) : (
              <div className="space-y-3">
                {data.orders.map((o) => (
                  <div
                    key={o._id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-3 px-3 sm:py-2 sm:px-3 bg-gray-50 rounded-lg border border-gray-100"
                  >
                    <div className="min-w-0">
                      <span className="font-medium text-gray-800">{o.orderNo}</span>
                      <span className="text-gray-500 text-sm ml-0 sm:ml-2 block sm:inline">{formatDateTime(o.dateTime)}</span>
                      {o.customerName && (
                        <span className="text-gray-500 text-sm ml-0 sm:ml-2 block sm:inline">· {o.customerName}</span>
                      )}
                    </div>
                    <span className="font-semibold text-gray-800 tabular-nums flex-shrink-0">SAR {Number(o.totalAmount || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
